import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SizedDialog } from '@/components/SizedDialog';
import { previewUrl } from '../api';
import { PREVIEW_GENERIC_MESSAGE, previewFailureText } from '../previewError';
import { fileKind } from '../lib/fileKind';
import { IconAction } from './IconAction';

// Render PDFs ourselves (pdf.js) so the viewer matches our theme — no browser
// PDF chrome. Vite bundles the worker via the `?url` import; react-pdf fetches
// the bytes (cookie-authed same-origin), so nothing is framed.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const PDF_OPTIONS = { withCredentials: true } as const;

/** Full-size modal viewer for ANY vault file, on our own background.
 *
 *  One dialog for every type, because the alternative is what this replaced: PDFs opened
 *  in-app, images had no viewer at all, and the only way to look at one was Download — which
 *  opens a browser tab AND records a download against a person who only wanted to look.
 *
 *  Fetches the inline (`?inline=1`) URL, which is audited as a VIEW. The server decides what
 *  may be served that way (PDF, docx rendered to PDF, raster images); anything else answers
 *  415 and is shown here as a plain "download it to open it", never as an empty dialog. */
export function FilePreviewDialog({
  open,
  onOpenChange,
  docRef,
  title,
  versionId,
  fileUrl,
  filename,
  contentType,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  docRef: string;
  title: string;
  versionId?: number;
  // Explicit inline URL overrides the default ref/version one — needed for collab VERSION
  // previews, whose inline URL carries ``version_kind=collab`` (see collab.versionDownloadUrl).
  fileUrl?: string;
  /** The file's own name and type, so the viewer knows which renderer to use. Absent (an
   *  agent-generated document with no file behind it) means PDF, which is what those are. */
  filename?: string | null;
  contentType?: string | null;
}) {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(840);
  // The viewer fetches the file itself, so a failure never reaches the api client or the global
  // toast. Kept so the message can say what actually happened (see previewError.ts).
  const [loadError, setLoadError] = useState<unknown>(null);
  // Reset with the file: a failure belongs to ONE image, and leaving it set would
  // show the error over the next document the viewer opens.
  const [imageFailed, setImageFailed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const file = useMemo(
    () => fileUrl ?? previewUrl(docRef, versionId),
    [fileUrl, docRef, versionId],
  );
  // Which renderer. A docx is served as a rendered PDF, so it reads as 'pdf' here; anything
  // the server will not serve inline (xlsx, zip, SVG…) says so rather than failing silently.
  const kind = useMemo(() => {
    const k = fileKind(contentType, filename);
    if (k === 'image') return 'image';
    if (k === 'pdf' || k === 'docx' || (!filename && !contentType)) return 'pdf';
    return 'unsupported';
  }, [contentType, filename]);
  useEffect(() => {
    // a new file, or a reopen, is a fresh attempt — for BOTH renderers. Leaving imageFailed set
    // would show one image's failure over the next document opened in the same dialog.
    setLoadError(null);
    setImageFailed(false);
  }, [file, open]);

  // Pages fill the viewer width responsively (capped so they stay readable).
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setPageWidth(Math.min(el.clientWidth - 48, 1200));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SizedDialog size="viewer" className="flex flex-col gap-0 p-0">
        <DialogHeader className="border-b border-line px-5 py-3">
          <DialogTitle className="truncate pr-8 text-[15px]">{title}</DialogTitle>
        </DialogHeader>
        <div
          ref={scrollRef}
          // overflow-x AUTO, not hidden: the page width is measured from this element, and if
          // that measurement is ever wrong — taken before layout settles, or while an ancestor
          // is wider than the viewport — hiding the overflow clips the document with no way to
          // reach the rest of it. Scrolling sideways is an ugly fallback; an unreadable page is
          // a dead end.
          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto bg-line-soft px-6 py-6"
        >
          {open && kind === 'image' && !imageFailed && (
            // The inline URL is audited as a VIEW and the server only serves raster types that
            // way, so this can never become a script-bearing document on our origin.
            <img
              src={file}
              alt={title}
              onError={() => setImageFailed(true)}
              className="mx-auto max-w-full rounded-md bg-white shadow-card"
            />
          )}
          {open && kind === 'image' && imageFailed && (
            // Without this the PDF path explained itself and the image path showed the browser's
            // broken-image icon and the alt text — a file that had been deleted, or a request the
            // rate limiter shed, looked like a corrupt picture.
            //
            // The generic sentence, not a status-specific one: `onError` on an <img> carries no
            // status code, so claiming a particular cause here would be a guess. Saying less and
            // being right beats saying more and being wrong.
            <p className="py-16 text-center text-[13px] text-muted">{PREVIEW_GENERIC_MESSAGE}</p>
          )}
          {open && kind === 'unsupported' && (
            <p className="py-16 text-center text-[13px] text-muted">
              This file type can&rsquo;t be shown here. Download it to open it.
            </p>
          )}
          {open && kind === 'pdf' && (
            <Document
              file={file}
              options={PDF_OPTIONS}
              onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
              onLoadError={setLoadError}
              loading={
                <p className="py-16 text-center text-[13px] text-muted">Loading preview…</p>
              }
              error={
                <p className="py-16 text-center text-[13px] text-muted">
                  {previewFailureText(loadError)}
                </p>
              }
              className="flex flex-col items-center gap-5"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i}
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="overflow-hidden rounded-md shadow-card"
                />
              ))}
            </Document>
          )}
        </div>
      </SizedDialog>
    </Dialog>
  );
}

/** A bordered eye button that opens the in-app PDF viewer. Drop-in next to the
 *  download affordance on any document (or a specific version via `versionId`). */
export function PreviewButton({
  docRef,
  title,
  versionId,
  className,
}: {
  docRef: string;
  title: string;
  versionId?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconAction label={`Preview ${title}`} title="Preview"
                  onClick={() => setOpen(true)} className={className}>
        <Eye className="size-3.5" aria-hidden="true" />
      </IconAction>
      <FilePreviewDialog
        open={open}
        onOpenChange={setOpen}
        docRef={docRef}
        title={title}
        versionId={versionId}
      />
    </>
  );
}
